import { PRIORITY, useKeys } from '../lib/keyboard.js';
import type { View } from '../lib/router.js';
import { useT, type Key } from '../i18n/index.js';

export type HelpContext = 'list' | 'record';

type Row = [string, Key];

const EVERYWHERE: Row[] = [
  ['?', 'help.cheatsheet'],
  ['q', 'help.ask'],
  ['1 2 3 4', 'help.views'],
  ['n', 'help.new'],
  ['/', 'help.search'],
  ['j / k  ↓ / ↑', 'help.move'],
  ['Enter', 'help.open'],
  ['Shift+Enter', 'help.openNotion'],
  ['Esc', 'help.esc'],
  ['u', 'help.undo'],
];

const LIST: Row[] = [
  ['s', 'help.filterStage'],
  ['a', 'help.filterAction'],
  ['t', 'help.filterStatus'],
  ['o', 'help.filterOwner'],
  ['i', 'help.mine'],
  ['d', 'help.due'],
  ['w', 'help.week'],
  ['m', 'help.month'],
  ['x', 'help.clear'],
  ['g', 'help.group'],
  ['h', 'help.collapse'],
  ['f z c', 'help.rowActions'],
];

const RECORD: Row[] = [
  ['s', 'help.setStage'],
  ['a', 'help.setAction'],
  ['t', 'help.setStatus'],
  ['o', 'help.setOwner'],
  ['i', 'help.assignMe'],
  ['d', 'help.date'],
  ['w', 'help.snooze7'],
  ['f', 'help.followedUp'],
  ['m', 'help.message'],
  ['z', 'help.snooze'],
  ['c', 'help.note'],
  ['e', 'help.edit'],
];

/** Keys that exist only in one view (§7 "context-aware cheat sheet"). */
const VIEW: Partial<Record<View, Row[]>> = {
  table: [['v', 'help.columns']],
  new: [
    ['Ctrl+Enter', 'help.save'],
    ['Tab', 'help.acceptMatch'],
  ],
  log: [['r', 'help.revertSelected']],
};

/** `?` cheat sheet (§7): shows the shortcuts of the active context and view. */
export function HelpOverlay({ context, view, onClose }: { context: HelpContext; view: View; onClose: () => void }) {
  const t = useT();
  useKeys({ Escape: onClose, '?': onClose }, { priority: PRIORITY.overlay });
  const Block = ({ title, rows }: { title: string; rows: Row[] }) => (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">{title}</h3>
      <dl className="space-y-1.5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-baseline gap-3 text-sm">
            <dt className="w-28 shrink-0 text-right">
              {k.split(' ').map((x, i) => (
                <kbd key={i} className="mr-1">
                  {x}
                </kbd>
              ))}
            </dt>
            <dd>{t(v)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
  const viewRows = context === 'list' ? VIEW[view] : undefined;
  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/30" onMouseDown={onClose} />
      <div className="fixed left-1/2 top-16 z-50 w-[min(760px,calc(100vw-32px))] -translate-x-1/2 rounded-2xl border border-line bg-paper p-6 shadow-2xl max-h-[calc(100vh-5rem)] overflow-y-auto">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{t('help.title')}</h2>
          <span className="text-xs text-muted">{t('help.context', { context: t(context === 'list' ? 'help.context.list' : 'help.context.record') })}</span>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <Block title={t('help.everywhere')} rows={EVERYWHERE} />
          <div className="space-y-6">
            <Block title={t(context === 'list' ? 'help.listTitle' : 'help.recordTitle')} rows={context === 'list' ? LIST : RECORD} />
            {viewRows && <Block title={t('help.viewTitle')} rows={viewRows} />}
          </div>
        </div>
      </div>
    </>
  );
}
